import { useEffect, useMemo, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
// ⚠️ On importe TON client axios configuré
import api from '../../lib/axios'
import { uploadImage, getApiError } from '../../lib/editorUtils'

/* ============================================================
   CLIENT API — basé sur ton axios.js
   ============================================================ */

const qrApi = {
  list: () => api.get('/qrs').then((r) => r.data),
  create: (payload) => api.post('/qrs', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/qrs/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/qrs/${id}`).then((r) => r.data),
}

/* ============================================================
   CONSTANTES
   ============================================================ */

const QR_TYPES = [
  { value: 'link',   label: 'Lien' },
  { value: 'menu',   label: 'Menu' },
  { value: 'review', label: 'Avis Google' },
  { value: 'vcard',  label: 'Carte de visite' },
  { value: 'text',   label: 'Texte' },
]

const EMPTY_FORM = {
  name: '',
  type: 'link',
  target_url: '',
  content: '',
  color: '#006b5f',
  logo_url: '',
}

// Taille du logo au centre : 22 % du QR max pour rester scannable
// (niveau de correction d'erreur "H" = jusqu'à ~30 % de zone masquée)
const LOGO_RATIO = 0.22
const MAX_LOGO_BYTES = 2 * 1024 * 1024

// On construit l'URL publique en se basant sur l'URL de l'API
const PUBLIC_BASE =
  (api.defaults.baseURL || 'https://api.tapiio.ma/api/v1').replace(/\/$/, '')

/* ============================================================
   QR AVEC LOGO (rendu côté navigateur)
   ============================================================ */

// Teste si le logo se charge en mode CORS. Sinon, on l'affiche quand même
// (sans CORS) : le logo apparaît, seul le téléchargement PNG sera bloqué.
function useLogoCrossOrigin(src) {
  const [mode, setMode] = useState('anonymous')

  useEffect(() => {
    if (!src) return undefined
    let alive = true
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => alive && setMode('anonymous')
    img.onerror = () => alive && setMode(undefined)
    img.src = src
    return () => {
      alive = false
    }
  }, [src])

  return mode
}

function QrWithLogo({ value, color, logoUrl, size = 512, id }) {
  const logoSize = Math.round(size * LOGO_RATIO)
  const crossOrigin = useLogoCrossOrigin(logoUrl)

  return (
    <QRCodeCanvas
      id={id}
      value={value}
      size={size}
      level="H"
      marginSize={2}
      fgColor={color || '#006b5f'}
      bgColor="#ffffff"
      imageSettings={
        logoUrl
          ? {
              src: logoUrl,
              width: logoSize,
              height: logoSize,
              excavate: true,
              crossOrigin,
            }
          : undefined
      }
      style={{ width: '100%', height: 'auto' }}
    />
  )
}

/* ============================================================
   COMPOSANT
   ============================================================ */

export default function GeneratorQR() {
  const [qrs, setQrs] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [search, setSearch] = useState('')
  const [copiedId, setCopiedId] = useState(null)
  const logoInputRef = useRef(null)

  const load = async () => {
    setLoading(true)
    try {
      const data = await qrApi.list()

      // Sécurité : si Laravel renvoie { message: "Unauthenticated." } au lieu d'un tableau
      if (!Array.isArray(data)) {
        throw new Error(data?.message || 'Réponse API invalide')
      }

      setQrs(data)
      setError('')
    } catch (e) {
      if (e.response?.status === 401) {
        setError('Session expirée ou non authentifiée — veuillez vous reconnecter.')
      } else {
        setError(e.response?.data?.message || e.message || 'Impossible de charger les QR codes.')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    if (!success && !error) return
    const t = setTimeout(() => {
      setSuccess('')
      setError('')
    }, 4000)
    return () => clearTimeout(t)
  }, [success, error])

  const filteredQrs = useMemo(() => {
    if (!search.trim()) return qrs
    const q = search.trim().toLowerCase()
    return qrs.filter(
      (qr) =>
        qr.name.toLowerCase().includes(q) ||
        qr.slug.toLowerCase().includes(q) ||
        qr.target_url.toLowerCase().includes(q)
    )
  }, [qrs, search])

  const resetForm = () => {
    setForm(EMPTY_FORM)
    setEditingId(null)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((f) => ({ ...f, [name]: value }))
  }

  const extractApiError = (e) => {
    const errs = e.response?.data?.errors
    if (errs) return Object.values(errs)[0]?.[0] || 'Erreur de validation'
    return e.response?.data?.message || 'Une erreur est survenue'
  }

  /* ---------- Logo ---------- */

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Le logo doit être une image (PNG, JPG, WebP ou SVG).')
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError('Le logo est trop lourd (2 Mo maximum).')
      return
    }

    setUploadingLogo(true)
    setError('')
    try {
      const url = await uploadImage(file, 'logo')
      setForm((f) => ({ ...f, logo_url: url }))
    } catch (err) {
      setError(getApiError(err, "Erreur lors de l'envoi du logo."))
    } finally {
      setUploadingLogo(false)
    }
  }

  const removeLogo = () => setForm((f) => ({ ...f, logo_url: '' }))

  /* ---------- CRUD ---------- */

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const payload = { ...form, logo_url: form.logo_url || null }
      if (editingId) {
        await qrApi.update(editingId, payload)
        setSuccess('QR code mis à jour.')
      } else {
        await qrApi.create(payload)
        setSuccess('QR code généré avec succès.')
      }
      resetForm()
      await load()
    } catch (e) {
      setError(extractApiError(e))
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (qr) => {
    setEditingId(qr.id)
    setForm({
      name: qr.name,
      type: qr.type,
      target_url: qr.target_url,
      content: qr.content || '',
      color: qr.color || '#006b5f',
      logo_url: qr.logo_url || '',
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce QR code ?')) return
    try {
      await qrApi.remove(id)
      setQrs((list) => list.filter((q) => q.id !== id))
      if (editingId === id) resetForm()
      setSuccess('QR code supprimé.')
    } catch (e) {
      setError(extractApiError(e))
    }
  }

  const handleToggleActive = async (qr) => {
    try {
      const updated = await qrApi.update(qr.id, { is_active: !qr.is_active })
      setQrs((list) => list.map((q) => (q.id === qr.id ? updated : q)))
    } catch (e) {
      setError(extractApiError(e))
    }
  }

  const publicUrl = (qr) => `${PUBLIC_BASE}/q/${qr.slug}`

  const copyPublicUrl = async (qr) => {
    const url = publicUrl(qr)
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(qr.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      window.prompt('Copiez ce lien :', url)
    }
  }

  // Télécharge le QR avec logo depuis le canvas
  const downloadQrWithLogo = (qr) => {
    const canvas = document.getElementById(`qr-canvas-${qr.id}`)
    if (!canvas) return
    try {
      const link = document.createElement('a')
      link.href = canvas.toDataURL('image/png')
      link.download = `${qr.slug}.png`
      link.click()
    } catch {
      setError(
        "Téléchargement impossible : le serveur doit autoriser le CORS sur les images du logo (/storage)."
      )
    }
  }

  // Aperçu en direct dans le formulaire
  const previewValue = form.target_url || 'https://tapio.ma'

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-navy">Générateur de QR codes</h1>
        <p className="text-gray-500 text-sm mt-1">
          Créez, modifiez et supprimez vos QR codes. Le slug et l'image sont
          générés automatiquement. Vous pouvez ajouter votre logo au centre.
        </p>
      </header>

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 text-green-700 border border-green-200 rounded-xl px-4 py-3 text-sm">
          {success}
        </div>
      )}

      {/* Formulaire */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 grid gap-4 md:grid-cols-2"
      >
        <div className="md:col-span-2 flex items-center justify-between">
          <h2 className="font-semibold text-navy">
            {editingId ? 'Modifier le QR code' : 'Nouveau QR code'}
          </h2>
          {editingId && (
            <span className="text-xs text-gray-500">Édition du #{editingId}</span>
          )}
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Nom *</span>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            maxLength={80}
            placeholder="Ex : Carte de visite restaurant"
            className="border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/40"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Type</span>
          <select
            name="type"
            value={form.type}
            onChange={handleChange}
            className="border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/40"
          >
            {QR_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label className="md:col-span-2 flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">URL cible *</span>
          <input
            name="target_url"
            type="url"
            value={form.target_url}
            onChange={handleChange}
            required
            placeholder="https://exemple.com/mon-menu"
            className="border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/40"
          />
          <span className="text-xs text-gray-400">
            Destination après scan du QR code.
          </span>
        </label>

        {(form.type === 'text' || form.type === 'vcard') && (
          <label className="md:col-span-2 flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700">
              Contenu (texte / vCard)
            </span>
            <textarea
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={3}
              className="border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
          </label>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Couleur</span>
          <div className="flex items-center gap-3">
            <input
              name="color"
              type="color"
              value={form.color}
              onChange={handleChange}
              className="h-10 w-14 border border-gray-200 rounded-xl cursor-pointer"
            />
            <input
              name="color"
              value={form.color}
              onChange={handleChange}
              pattern="^#[0-9A-Fa-f]{6}$"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
          </div>
        </label>

        {/* Logo au centre du QR */}
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Logo au centre</span>
          <div className="flex items-center gap-3">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleLogoFile}
              className="hidden"
            />
            <div className="h-14 w-14 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-[10px] text-gray-400">Aucun</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                {uploadingLogo
                  ? 'Envoi…'
                  : form.logo_url
                    ? 'Changer le logo'
                    : 'Ajouter un logo'}
              </button>
              {form.logo_url && (
                <button
                  type="button"
                  onClick={removeLogo}
                  className="px-3 py-1.5 rounded-lg text-sm bg-red-50 text-red-600 hover:bg-red-100"
                >
                  Retirer
                </button>
              )}
            </div>
          </div>
          <span className="text-xs text-gray-400">
            PNG, JPG, WebP ou SVG, 2 Mo max. Un logo carré sur fond transparent
            ou blanc donne le meilleur résultat.
          </span>
        </div>

        {/* Aperçu en direct */}
        <div className="md:col-span-2 flex items-center gap-4 rounded-xl bg-gray-50 border border-gray-100 p-4">
          <div className="w-28 shrink-0 bg-white rounded-lg p-1">
            <QrWithLogo
              value={previewValue}
              color={form.color}
              logoUrl={form.logo_url}
              size={256}
            />
          </div>
          <p className="text-xs text-gray-500">
            Aperçu. Le QR pointe directement vers l'URL cible. Testez toujours
            le scan avec votre téléphone avant d'imprimer.
          </p>
        </div>

        <div className="md:col-span-2 flex gap-3 justify-end pt-2">
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Annuler
            </button>
          )}
          <button
            type="submit"
            disabled={saving || uploadingLogo}
            className="px-6 py-2 rounded-xl bg-teal text-white font-semibold disabled:opacity-50 hover:bg-teal/90"
          >
            {saving
              ? 'Enregistrement…'
              : editingId
                ? 'Mettre à jour'
                : 'Générer le QR code'}
          </button>
        </div>
      </form>

      {/* Liste */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-3">
          <h2 className="font-semibold text-navy">
            Mes QR codes ({qrs.length})
          </h2>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-teal/40"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-2 border-[#006b5f]/30 border-t-[#006b5f] rounded-full animate-spin" />
          </div>
        ) : filteredQrs.length === 0 ? (
          <p className="text-gray-500 text-sm">
            {qrs.length === 0
              ? 'Aucun QR code pour le moment.'
              : 'Aucun résultat pour cette recherche.'}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredQrs.map((qr) => (
              <article
                key={qr.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-navy truncate">
                      {qr.name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {qr.type} · {qr.scans} scan{qr.scans > 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleActive(qr)}
                    className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                      qr.is_active
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {qr.is_active ? 'actif' : 'inactif'}
                  </button>
                </div>

                {qr.logo_url ? (
                  // QR avec logo : rendu côté navigateur
                  <div className="w-full aspect-square bg-white rounded-xl border border-gray-100 p-2">
                    <QrWithLogo
                      id={`qr-canvas-${qr.id}`}
                      value={qr.target_url}
                      color={qr.color}
                      logoUrl={qr.logo_url}
                      size={512}
                    />
                  </div>
                ) : qr.qr_image_url ? (
                  <img
                    src={qr.qr_image_url}
                    alt={`QR ${qr.name}`}
                    className="w-full aspect-square object-contain bg-gray-50 rounded-xl"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full aspect-square bg-gray-50 rounded-xl flex items-center justify-center text-gray-400 text-xs">
                    Aucune image
                  </div>
                )}

                <p className="text-xs text-gray-500 truncate">{qr.target_url}</p>
                <p className="text-[11px] text-gray-400 font-mono truncate">
                  /q/{qr.slug}
                </p>

                <div className="flex flex-wrap gap-2 mt-auto">
                  <button
                    onClick={() => handleEdit(qr)}
                    className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(qr.id)}
                    className="px-3 py-1.5 rounded-lg text-sm bg-red-50 text-red-600 hover:bg-red-100"
                  >
                    Supprimer
                  </button>
                  <button
                    onClick={() => copyPublicUrl(qr)}
                    className="px-3 py-1.5 rounded-lg text-sm bg-teal/10 text-teal hover:bg-teal/20"
                  >
                    {copiedId === qr.id ? 'Copié ✓' : 'Copier le lien'}
                  </button>
                  {qr.logo_url ? (
                    <button
                      onClick={() => downloadQrWithLogo(qr)}
                      className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      Télécharger
                    </button>
                  ) : (
                    qr.qr_image_url && (
                      <a
                        href={qr.qr_image_url}
                        download={`${qr.slug}.png`}
                        className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        Télécharger
                      </a>
                    )
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}